import type React from 'react';
import { useNavigate } from 'react-router-dom';
import { Table } from '@/components/ui';
import OrderStatusBadge from './OrderStatusBadge';
import type { Order } from '@/types/models';
import { ChevronRight, MapPin, User } from 'lucide-react';

interface OrdersTableProps {
  orders: Order[];
  isLoading?: boolean;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '—';
  }
}

function formatShortName(fullName: string | null | undefined): string {
  if (!fullName) return '—';
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0];
  const surname = parts[0];
  const initials = parts.slice(1).map(p => `${p[0]}.`).join('');
  return `${surname} ${initials}`;
}

const OrdersTable: React.FC<OrdersTableProps> = ({ orders }) => {
  const navigate = useNavigate();

  return (
    <>
      {/* Mobile: card view */}
      <div className="md:hidden divide-y divide-gray-100">
        {orders.map((order) => (
          <button
            key={order.id}
            onClick={() => navigate(`/orders/${order.id}`)}
            className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50/50 transition-colors text-left"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-sm font-semibold text-gray-900 truncate">{order.order_number}</p>
                <OrderStatusBadge status={order.status} />
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span>{formatDate(order.created_at)}</span>
                {order.customer_name && (
                  <span className="inline-flex items-center gap-0.5 truncate">
                    <User className="w-3 h-3" />
                    {formatShortName(order.customer_name)}
                  </span>
                )}
                {order.invoice_number && (
                  <span className="truncate">Накл. {order.invoice_number}</span>
                )}
                {order.shelf_location && (
                  <span className="inline-flex items-center gap-0.5">
                    <MapPin className="w-3 h-3" />
                    {order.shelf_location}
                  </span>
                )}
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
          </button>
        ))}
      </div>

      {/* Desktop: table view */}
      <div className="hidden md:block">
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.Head>Номер заказа</Table.Head>
              <Table.Head>Клиент</Table.Head>
              <Table.Head>Статус</Table.Head>
              <Table.Head>Накладная</Table.Head>
              <Table.Head>Дата создания</Table.Head>
              <Table.Head>Дата приёмки</Table.Head>
              <Table.Head>Полка</Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {orders.map((order) => (
              <Table.Row
                key={order.id}
                onClick={() => navigate(`/orders/${order.id}`)}
              >
                <Table.Cell className="font-medium text-gray-900">
                  {order.order_number}
                </Table.Cell>
                <Table.Cell className="text-gray-700">
                  {formatShortName(order.customer_name)}
                </Table.Cell>
                <Table.Cell>
                  <OrderStatusBadge status={order.status} />
                </Table.Cell>
                <Table.Cell>
                  {order.invoice_number || '—'}
                </Table.Cell>
                <Table.Cell>
                  {formatDate(order.created_at)}
                </Table.Cell>
                <Table.Cell>
                  {formatDate(order.received_at)}
                </Table.Cell>
                <Table.Cell>
                  {order.shelf_location ? (
                    <span className="inline-flex items-center gap-1 text-sm">
                      <MapPin className="w-3.5 h-3.5 text-gray-400" />
                      {order.shelf_location}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    </>
  );
};

export default OrdersTable;
